import {PrivacyText} from '@/components/PrivacyText/PrivacyText';
import {yupResolver} from '@hookform/resolvers/yup';
import React from 'react';
import {useForm} from 'react-hook-form';
import * as yup from 'yup';
import {FormButton} from '../FormButton/FormButton';
import {FormInput} from '../FormInput/FormInput';
import {FormOptionGroup, FormSelect, FormSelectOption} from '../FormSelect/FormSelect';
import {LocationFormSelect} from '../LocationFormSelect/LocationFormSelect';

export type FormFieldConfig = {
  name: string;
  type: string;
  placeholder: string;
  validation: yup.AnySchema;
  spellCheck?: boolean;
  options?: FormSelectOption[];
  optionGroups?: FormOptionGroup[];
};

export type FormData = Record<string, string>;

type FormFieldsProps = {
  ctaButtonText?: string;
  onSubmit: (data: FormData) => void;
  errorMessage?: string;

  fields: FormFieldConfig[];
  isLoading?: boolean;
  defaultValues?: FormData;
};

export const CommonRegistrationForm = ({
  ctaButtonText = 'Continue',
  onSubmit,
  errorMessage,
  fields,
  isLoading = false,
  defaultValues,
}: FormFieldsProps) => {
  const formSchema = yup.object().shape(
    fields.reduce((acc, field) => {
      acc[field.name] = field.validation;
      return acc;
    }, {} as Record<string, yup.AnySchema>)
  );

  const {
    register,
    handleSubmit,
    formState: {errors},
    setValue,
  } = useForm<FormData>({
    mode: 'onBlur',
    resolver: yupResolver(formSchema),
    defaultValues,
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grow w-full">
      {fields.map((field) => (
        <React.Fragment key={field.name}>
          {field.type === 'select' ? (
            field.name === 'location' ? (
              <LocationFormSelect
                register={register}
                hasError={!!errors[field.name]?.message}
                setValue={setValue}
                fieldName={field.name}
              />
            ) : (
              <FormSelect
                {...register(field.name)}
                id={field.name}
                options={field.options}
                optionGroups={field.optionGroups}
                placeholderValue={field.placeholder}
                hasError={!!errors[field.name]?.message}
              />
            )
          ) : (
            <FormInput
              spellCheck={field.spellCheck ?? false}
              {...register(
                field.name,
                field.name === 'email' ? {setValueAs: (val: string) => val.toLowerCase()} : undefined
              )}
              type={field.type}
              id={field.name}
              placeholder={field.placeholder}
              hasError={!!errors[field.name]?.message}
            />
          )}
          <p className="text-xs text-alert mb-1 ml-1">{(errors[field.name]?.message as string) ?? '\u00A0'}</p>
        </React.Fragment>
      ))}

      <div className="h-[13px] block" />

      <p className="text-xs text-alert mb-1">{errorMessage ?? '\u00A0'}</p>

      <PrivacyText className="mb-4" actionButtonText={ctaButtonText} />

      <FormButton type="submit" isPrimary hasError={Object.keys(errors).length !== 0} disabled={isLoading}>
        {isLoading ? 'Loading...' : ctaButtonText}
      </FormButton>
    </form>
  );
};
